import { useDispatch, useSelector } from "../store/store";
import { authorizeActions, authorizeSelectors } from "../store/authorize";
import { useNavigate } from "react-router-dom";
import { useGuard } from "../Guard";
import { Avatar, Badge, Button, Dropdown, Tooltip, Typography } from "antd";
import { softColor } from "../utils/colors";
import Icon from "@ant-design/icons";
import { Icons } from "./../assets/icons";
import { authLogout, getAvatar } from "../repository/auth.repository";
import { memo, useEffect, useSyncExternalStore } from "react";
import BlankAvatar from "../assets/images/blank-avatar.svg";

export interface LoggedUserProps {}

function LoggedUser(props: LoggedUserProps) {
  const dispatch = useDispatch();
  const user = useSelector(authorizeSelectors.user);
  const navigate = useNavigate();
  const guard = useGuard();

  // Lifecycle

  useEffect(() => {
    getAvatar()
      .catch(() => BlankAvatar)
      .then((data) => {
        // Partial merge — order-independent vs the profile load (no stale-user clobber).
        dispatch(authorizeActions.setAvatar(data));
      });
  }, [dispatch]);

  // Events

  function onMenuItem(e: { readonly key: string }): void {
    switch (e.key) {
      case "profile":
        navigate("profile", { replace: true });
        break;
      case "my-calendar":
        navigate("my-calendar", { replace: true });
        break;
      case "signOut":
        authLogout();
        guard.signOut();
        break;
    }
  }

  function getFullName() {
    if ([user?.firstName, user?.lastName].filter((f) => !!f).length) {
      return [user?.firstName, user?.lastName].join(" ");
    }

    return user?.userName ?? "";
  }

  const userName = getFullName();

  // Live hub status via an external store — only this dot re-renders on a hub transition, not the
  // whole header. green = connected, gold = connecting/reconnecting, red = offline.
  const connectionState = useSyncExternalStore(
    guard.subscribeConnection,
    guard.getConnectionState,
  );
  const liveness = {
    connected: { color: "green", text: "Live" },
    connecting: { color: "gold", text: "Connecting…" },
    disconnected: { color: "red", text: "Offline" },
  }[connectionState];

  return (
    <Dropdown
      trigger={["click"]}
      overlayClassName={"min-w-64"}
      menu={{
        items: [
          {
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.AssignmentIcon}
              />
            ),
            key: "profile",
            label: "My Profile",
          },
          {
            icon: (
              <Icon
                className="-ml-0.5 text-2xl"
                component={Icons.CalendarMonthIcon}
              />
            ),
            key: "my-calendar",
            label: "My Calendar",
          },
          {
            icon: (
              <Icon className="-ml-0.5 text-2xl" component={Icons.LogoutIcon} />
            ),
            key: "signOut",
            label: "Sign Out",
          },
        ],
        onClick: onMenuItem,
      }}
    >
      <Button className="m-0 p-1" type={"text"} size={"large"}>
        <Tooltip title={liveness.text}>
          <Badge color={liveness.color} dot>
            <Avatar
              style={{
                backgroundColor: user?.avatarSrc
                  ? "lightgray"
                  : softColor((userName[0] ?? "").toUpperCase()),
              }}
              className="flex flex-col items-center justify-center text-lg font-bold"
              src={user?.avatarSrc}
            >
              {(userName[0] ?? "").toUpperCase()}
            </Avatar>
          </Badge>
        </Tooltip>
        <Typography.Text className="max-w-72 whitespace-nowrap">
          {userName}
        </Typography.Text>
      </Button>
    </Dropdown>
  );
}

export default memo(LoggedUser);
